import styled, { css } from 'styled-components';

import theme from '../../assets/theme';
import { LessonAttachment } from '../../services/backendService/backendService';
import Poll from '../_organisms/Poll';
import { Slide, SlideElement } from './slide.types';
import SlideElementAttachmentPassage from './SlideElementAttachmentPassage';
import SlideElementIframe from './SlideElementIframe';
import SlideElementImage from './SlideElementImage';
import SlideElementLoader from './SlideElementLoader';
import SlideElementShape from './SlideElementShape';
import SlideElementSvg from './SlideElementSvg';
import SlideElementText from './SlideElementText';
import {
  isSlideElementAttachmentPassage,
  isSlideElementIframe,
  isSlideElementImage,
  isSlideElementLoader,
  isSlideElementShape,
  isSlideElementSvg,
  isSlideElementText,
} from './slideUtil';

const SlideViewer = ({
  elements,
  slide,
  showStaticPoll,
  showTextPlaceHolder,
  isDiscoverTemplate = false,
  showPollOptions,
  isAuthenticated,
  staticPollResult,
  isOwner,
  lessonAttachments,
  gameId,
}: {
  elements: SlideElement[];
  slide: Slide;
  staticPollResult: number[] | undefined;
  showStaticPoll?: boolean;
  showTextPlaceHolder?: boolean;
  isDiscoverTemplate?: boolean;
  isAuthenticated?: boolean;
  isOwner: boolean;
  showPollOptions?: boolean;
  lessonAttachments?: LessonAttachment[];
  gameId?: string;
}) => {
  return (
    <>
      {elements?.map(element => (
        <SlideElementContainer
          key={element.id}
          $selected={false}
          $height={element.height}
          $left={element.x}
          $top={element.y}
          $width={element.width}
          $angle={element.angle}
          $opacity={element.opacity}
        >
          <div
            style={{
              width: element.width / (element.scale || 1),
              height: element.height / (element.scale || 1),
              transform: `scale(${(element.scale || 1) *
                (element.flip?.x ? -1 : 1)}, ${(element.scale || 1) *
                (element.flip?.y ? -1 : 1)})`,
              transformOrigin: element.flip ? 'center' : 'top left',
            }}
          >
            {isSlideElementImage(element) && (
              <SlideElementImage
                element={element}
                isAuthenticated={!!isAuthenticated}
                isDiscoverTemplate={isDiscoverTemplate}
                isOwner={isOwner}
              />
            )}
            {isSlideElementSvg(element) && (
              <SlideElementSvg
                element={element}
                isAuthenticated={!!isAuthenticated}
                isOwner={isOwner}
                isDiscoverTemplate={isDiscoverTemplate}
              />
            )}
            {isSlideElementShape(element) && <SlideElementShape element={element} />}
            {isSlideElementIframe(element) && (
              <SlideElementIframe element={element} slideMode={'view'} />
            )}

            {isSlideElementText(element) && (
              <SlideElementText
                element={element}
                richTextEditorPrefix={'slide-viewer'}
                showPlaceHolder={showTextPlaceHolder}
                content={element.textValue}
              />
            )}
            {isSlideElementLoader(element) && <SlideElementLoader gameId={gameId} />}
            {isSlideElementAttachmentPassage(element) && (
              <SlideElementAttachmentPassage
                element={element}
                slideMode="view"
                lessonAttachments={lessonAttachments}
                isFromDiscover={isDiscoverTemplate}
              />
            )}
          </div>
        </SlideElementContainer>
      ))}
      {slide.type === 'poll' && slide.pollOptions && showStaticPoll && showPollOptions && (
        <PollContainer>
          <Poll
            staticData={slide.pollOptions.map((e, i) => ({
              ...e,
              count: staticPollResult?.[i] || 0,
            }))}
            isAuthenticated={!!isAuthenticated}
            hideCount={true}
          />
        </PollContainer>
      )}
    </>
  );
};

const SlideElementContainer = styled.div<{
  $selected?: boolean;
  $height?: number;
  $width: number;
  $top?: number;
  $left?: number;
  $angle?: number;
  $opacity?: number;
}>`
  position: absolute;
  top: ${({ $top }) => `${$top}px` || '0'};
  left: ${({ $left }) => `${$left}px` || '0'};
  background-color: 'transparent';
  border-radius: 10px;
  width: ${({ $width }) => ($width ? `${$width}px` : 'auto')};
  height: ${({ $height }) => ($height ? `${$height}px` : 'auto')};
  transform: ${({ $angle }) => `rotate(${$angle || 0}deg)`};
  transform-origin: center;
  opacity: ${({ $opacity }) => ($opacity !== undefined ? $opacity : 1)};
  p,
  input {
    margin: 0;
    border: none;
    outline: none;
  }

  ${({ $selected }) =>
    $selected &&
    css`
      outline: 4px solid ${theme.colors.orange};
    `};
`;

const PollContainer = styled.div`
  position: absolute;
  width: 90%;
  height: 10%;
  background-color: transparent;
  bottom: 25%;
  left: 5%;
`;

export default SlideViewer;
